import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import CopyIcon from '@material-ui/icons/FileCopy';
import Editor, { TABLE_CELL_COMPONENT, FORM_FIELD_COMPONENT } from 'tuc-webui-base/components/editors/Editor';
import { ObjectIdTableCell } from './ObjectIdEditor';
import './ObjectListEditor.scss';

const makeComponent = (options = {}, form) => {
  const {
    getId = o => o.id,
    getIcon,
    getLabel,
    getLink,
    getLinkTooltip,
    getObjects,
    getCopyyValue,
    getCopyTooltip,
    getClassName,
    noItems,
    tableNoItems,
  } = options;

  let Component;
  if (form) {
    const FormListItem = ({ id, item, history }) => {
      const copyValue = getCopyyValue && getCopyyValue(id, item);
      const copyTooltip = getCopyTooltip && getCopyTooltip(id, item);
      const copyToClipboard = useCallback(() => {
      // API is well supported: https://developer.mozilla.org/en-US/docs/Web/API/Clipboard/writeText
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(copyValue);
        }
      }, [copyValue]);

      const link = getLink && getLink(id, item);
      const linkTooltip = getLinkTooltip && getLinkTooltip(id, item);
      const handleNavObject = () => {
        if (link) {
          history.push(link);
        }
      };

      const Icon = getIcon && getIcon(id, item);
      const label = getLabel && getLabel(id, item, link ? 2 : 1) || id;

      let labelCmp;
      if (link) {
        labelCmp = (
          <Tooltip title={linkTooltip}>
            <Button
              component={Link}
              color="primary"
              onClick={handleNavObject}
              className="grb-form-object-list-link"
            >
              {Array.isArray(label) ? label[0] : label}
            </Button>
          </Tooltip>
        );
      } else if (Array.isArray(label)) {
        labelCmp = (
          <div className="grb-form-object-list-item-label-ctnr">
            <Typography variant="body1" className="grb-form-object-list-item-label-primary">{label[0]}</Typography>
            <Typography variant="body2" className="grb-form-object-list-item-label-secondary">{label[1]}</Typography>
          </div>
        );
      } else {
        labelCmp = (
          <Typography variant="body1" className="grb-form-object-list-label">
            {label}
          </Typography>
        );
      }

      return (
        <div className="grb-form-object-list-item" key={id}>
          {!!Icon && <Icon className="grb-form-object-list-item-icon" />}
          {labelCmp}
          {!!copyValue && (
            <Tooltip title={copyTooltip}>
              <IconButton size="small" onClick={copyToClipboard}>
                <CopyIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      );
    };

    FormListItem.propTypes = {
      id: PropTypes.string.isRequired,
      item: PropTypes.oneOfType([
        PropTypes.shape({}),
        PropTypes.string,
      ]).isRequired,
      history: PropTypes.shape({
        push: PropTypes.func,
      }).isRequired,
    };

    const FormList = (props) => {
      const { data, value } = props;
      const objects = getObjects ? getObjects(data) : value;

      if (!objects || !objects.length) {
        return (
          <div className="grb-form-object-list">
            <Typography className="grb-form-object-list-none">
              {noItems}
            </Typography>
          </div>
        );
      }

      return (
        <div className="grb-form-object-list">
          {objects.map((o) => {
            const id = getId(o);
            return (
              <FormListItem
                {...props}
                id={id}
                item={o}
                key={id}
                getLink={getLink}
                getLabel={getLabel}
                getClassName={getClassName}
              />
            );
          })}
        </div>
      );
    };

    FormList.propTypes = {
      data: PropTypes.shape({}),
      value: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.shape({}),
        PropTypes.string,
      ])),
    };

    FormList.defaultProps = {
      value: undefined,
      data: {},
    };
    Component = FormList;
  } else {
    const ListTableCell = (props) => {
      const { value, object } = props;
      const objects = getObjects ? getObjects(object) : (value || []);
      return (
        <div className="grb-tablecell-objects-list">
          <div className="grb-tablecell-objects-list-content">
            {!!objects.length && objects.map(o => (
              <ObjectIdTableCell
                {...props}
                key={getId(o)}
                value={getId(o)}
                object={o}
                getLink={getLink}
                getLabel={getLabel}
                getClassName={getClassName}
                getCopyValue={getCopyyValue}
                getCopyTooltip={getCopyTooltip}
              />
            ))}
            {!objects.length && tableNoItems && !!noItems && (
              <span>{noItems}</span>
            )}
          </div>
        </div>
      );
    };
    ListTableCell.propTypes = {
      value: PropTypes.oneOfType([
        PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({}),
          ]),
        ),
        PropTypes.shape({}),
      ]),
      object: PropTypes.shape({}).isRequired,
    };

    ListTableCell.defaultProps = {
      value: [],
    };
    Component = ListTableCell;
  }
  return Component;
};

const fillArrayWith = (o, count) => {
  const ar = new Array(count);
  for (let i = 0; i < count; i += 1) {
    ar[i] = o;
  }
  return ar;
};

export default class ObjectListEditor extends Editor {
  constructor(name, options) {
    super(name || 'containerId', Object.assign({}, {
      [TABLE_CELL_COMPONENT]: fillArrayWith(makeComponent(options), 2),
      [FORM_FIELD_COMPONENT]: fillArrayWith(makeComponent(options, true), 1),
    }));
    const { getId = o => o.id, getLabel, getFilterValues } = options;
    if (getFilterValues) {
      // this.filterValue = (array) => (array || []).reduce((total, o) => {
      //   return total.concat(getFilterValues(o));
      // }, []);
      this.filterValue = (value, fvalue, col, obj) => {
        const array = options.getObjects ? options.getObjects(obj) : value;
        const values = (array || []).reduce((total, o) => {
          return total.concat(getFilterValues(o));
        }, []);
        return values.join(',');
      };
    } else {
      this.filterValue = (array) => {
        const values = (array || []).map(o => getLabel(getId(o), o));
        return values.join(',');
      };
    }
  }

  formatValue(value /* , data, propName, props */) {
    return value;
  }

  getFilterValue(value, fvalue, col, obj) {
    return this.filterValue(value, fvalue, col, obj);
  }

  getSortValue(value /* ,formattedValue, props */) {
    return value;
  }
}
